import { useNavigate, useParams } from 'react-router-dom'
import { medicoConstants } from '../../data/types/medicos.types'
import { Medico, Role, Status } from '../../data/types/medico.types'
import { useEffect, useState } from 'react'
import { useEspecialidades } from '../../data/hooks/useEspecialidades.hook'
import { Spinner } from '../../components/spinner/Spinner.component'
import { SmartForm } from '../../components/formFields/Form'
import { MultiSelectDropdown } from '../../components/formFields/multiselectDropdown/MultiselectDropdown'
import { TextField } from '../../components/formFields/TextField'
import toast from 'react-hot-toast'
import { api } from '../../data/api'
import { CurrencyField } from '../../components/formFields/currencyField/CurrencyField'
import { DownloadFile } from '../../components/downloadFile/DownloadFile'
import { EntityConstants } from '../../helpers/general.helper'
import { fileToBase64, getMimeTypeBasedOnFilename } from '../../helpers/files.helper'
import { DropdownOption, FixedDropdown } from '../../components/formFields/fixedDropdown/FixedDropdown'
import { getUserFromToken, useAuth } from '../../auth/AuthContext'
import { FileUpload } from '../../components/fileUpload/FileUpload.component'
 
interface Entity extends Medico {
	especialidadesIds: string[]
}
 
const entityConstants: EntityConstants = {
	...medicoConstants,
	apiEndpoint: '/api/medicos',
	navigateToParent: '/med/admin/medicos'
}
 
const statusOptions: DropdownOption[] = [
	{
		id: Status.APROBADO,
		label: 'Aprobado'
	},
	{
		id: Status.RECHAZADO,
		label: 'Rechazado'
	},
	{
		id: Status.PENDIENTE,
		label: 'Pendiente'
	},
]

const AutorizacionMP = ({handleAuthorize, authorized = false, disabled = false}: {handleAuthorize: Function, authorized?: boolean, disabled?: boolean}) => {

	return (
		<>
			{(!authorized && !disabled) && (
				<div style={{ margin: '20px 0' }}>
					<a
						onClick={(e) => {
							e.preventDefault();
							handleAuthorize();
						}}
						href="#"
					>
						Autorizar en Mercado Pago
					</a>
				</div>
			)}
			{(authorized && !disabled) && <p style={{ color: 'green' }}>Autorizado en Mercado Pago</p>}
			{(!authorized && disabled) && <p style={{ color: 'red' }}>Falta Autorizar en Mercado Pago!</p>}
		</>
	)
}

const AutorizacionGoogle = ({
	handleAuthorize, 
	authorized = false, 
	disabled = false
}: {
	handleAuthorize: Function, 
	authorized?: boolean, 
	disabled?: boolean
}) => {

	return (
		<>
			{(!authorized && !disabled) && (
				<div style={{ margin: '20px 0' }}>
					<a
						onClick={(e) => {
							e.preventDefault();
							handleAuthorize();
						}}
						href="#"
					>
						Autorizar en Google
					</a>
				</div>
			)}
			{(authorized && !disabled) && <p style={{ color: 'green' }}>Autorizado en Google</p>}
			{(!authorized && disabled) && <p style={{ color: 'red' }}>Falta Autorizar en Google!</p>}
		</>
	)
}

export const AdminMedico = () => {
	const { dni: dniParam } = useParams();
	const { role, token } = useAuth();
	const dni = role === Role.ADMIN ? dniParam : getUserFromToken(token || '')?.dni || '';
 
	const navigate = useNavigate()
 
	const {data: dataEspecialidades, isPending: especPending, isError: especIsError, error: especError } = useEspecialidades()
 
	const [formData, setFormData] = useState<Partial<Entity & {cvTemp: File | null, mpToken: boolean, googleToken: boolean}>>({
		dni: '',
		apellido: '',
		nombre: '',
		email: '',
		matricula: '',
		telefono: '',
		status: Status.PENDIENTE,
		role: Role.MEDICO,
		especialidades: '',
		especialidadesIds: [],
		cvName: undefined,
		cvBase64: '',
		tarifa: 0,
		mpToken: false,
		googleToken: false,
		cvTemp: null,
	})
 
	const handleMpAuthorize = async () => {
		const urlToAutorize = await api.get('/api/payments/authorize')
		window.open(urlToAutorize.data.url)
	}

	const handleGoogleAuthorize = async () => {
		const urlToAutorize = await api.get('/api/google/authorize')
		window.open(urlToAutorize.data.url)
	}

	useEffect(() => {
		if (dni) {
		  const loadData = async () => {
			try {
				setLoading(true);
			  	const {data} = await api.get<Entity[]>(`${entityConstants.apiEndpoint}/${dni}`);
			  	const response: Entity & {mpToken: boolean, googleToken: boolean} = data as any;

				setFormData({
					dni: response.dni,
					role: response.role,
					nombre: response.nombre,
					apellido: response.apellido,
					email: response.email,
					matricula: response.matricula,
					status: response.status,
					telefono: response.telefono,
					tarifa: response.tarifa || 0,
					cvName: response.cvName,
					cvBase64: response.cvBase64,
					cvTemp: null,
					especialidadesIds: response.especialidades ? JSON.parse(response.especialidades).map((id: number) => id.toString(10)) || [] : [],
					especialidades: response.especialidades || '[]',
					mpToken: response.mpToken || false,
					googleToken: response.googleToken || false,
				});
			} catch (err) {
				console.error("### error", err);
				toast.error(`Error cargando ${entityConstants.entityLabelGender === 'M' ? 'el' : 'la'} ${entityConstants.entityLabel.toLowerCase()}`);
			} finally {
			  	setLoading(false);
			}
		  };
		  loadData();
		}
	}, [dni]);
 
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string | undefined>>({});
 
	const validateForm = () => {
		return true
	}
 
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
	 
		if (!validateForm()) return;
 
		try {
			await api.post(`${entityConstants.apiEndpoint}/${formData.dni}`, {
				especialidades: formData.especialidades,
				tarifa: formData.tarifa,
				status: formData.status,
				cvBase64: formData.cvTemp ? await fileToBase64(formData.cvTemp) : formData.cvBase64,
				cvName: formData.cvTemp?.name || formData.cvName,
				cvTemp: undefined,
			})
			toast.success(`${entityConstants.entityLabel} ${entityConstants.entityLabelGender === 'M' ? ' guardado' : ' guardada'}`);
			navigate(role === Role.MEDICO ? '/med/calendario' : '/med/admin/medicos');
		} catch (error) {
			console.error("### error", error);
			toast.error(`Error guardando ${entityConstants.entityLabel}`);
		}
 
	}
 
	if (loading || especPending) return <Spinner />
 
	return (
		<SmartForm
			title={'Perfil del Médico'}
			onSubmit={handleSubmit}
			cancelButtonNavigateTo={entityConstants.navigateToParent}
			confirmButtonLabel={'Guardar'}
		>
			<TextField
				label='DNI'
				value={formData.dni || ''}
				disabled={true}
			/>
			<TextField
				label='Nombre'
				value={formData.nombre || ''}
				disabled={true}
			/>
			<TextField
				label='Apellido'
				value={formData.apellido || ''}
				disabled={true}
			/>
			<TextField
				label='Matrícula'
				value={formData.matricula || ''}
				disabled={true}
			/>
			<MultiSelectDropdown 
				label='Especialidades'
				options={dataEspecialidades?.map(d => ({
					id: d.id.toFixed(0),
					label: d.nombre
				})) as any || []}
				value={formData.especialidadesIds || []}
				onChange={(value) => setFormData({
					...formData, 
					especialidadesIds: value,
					especialidades: JSON.stringify(value)
				})}
				error={errors.especialidades}
			/>
			<TextField
				label='Email'
				value={formData.email || ''}
				disabled={role === Role.ADMIN}
			/>
			<TextField
				label='Teléfono'
				value={formData.telefono || ''}
				disabled={role === Role.ADMIN}
			/>
			{(formData.cvName && formData.cvBase64) && <DownloadFile
				fileName={formData.cvName!}
				base64Content={formData.cvBase64!}
				mimeType={getMimeTypeBasedOnFilename(formData.cvName!)}
			/>}
			{role === Role.MEDICO && <FileUpload
				label='Curriculum'
				value={formData.cvTemp || null}
				onChange={(newFile) => {
					setFormData({...formData, cvTemp: newFile})
				}}
				accept='.pdf,.doc,.docx'
				maxSize={5}
				// errors={errors.documento}
			/>}
			{role === Role.ADMIN && <FixedDropdown 
				label='Status'
				onChange={(value: any) => setFormData({...formData, status: value})}
				options={statusOptions}
				value={formData.status!}
 
			/>}
			{formData.role === Role.MEDICO && <AutorizacionMP 
				handleAuthorize={handleMpAuthorize}
				disabled={role === Role.ADMIN}
				authorized={formData.mpToken}
			/>}
			{formData.role === Role.MEDICO && <AutorizacionGoogle 
				handleAuthorize={handleGoogleAuthorize}
				disabled={role === Role.ADMIN}
				authorized={formData.googleToken}
			/>}
			{role === Role.ADMIN && <CurrencyField 
				label='Tarifa'
				value={formData.tarifa || 0}
				currencySymbol='$'
				decimals={0}
				error={errors.tarifa}
				onChange={(value) => setFormData({...formData, tarifa: value})}
				disabled={role !== Role.ADMIN}
			/>}
		</SmartForm>
	)
}
